/**
 * Per-module permission rules.
 *
 * Role permissions (see permissions.ts) answer "may this role do X at all?".
 * These answer "may this user do X to THIS specific module?" — ownership and
 * sharing are per-record, so they cannot be expressed in the role matrix alone.
 *
 * Rules
 * ─────
 *  Admin        → full control of every module (admin override).
 *  Owner        → view, edit, rename, delete their own module. Can share it
 *                 too, but only given the `modules:share` permission — the
 *                 caller passes whether they hold it; see `canShareModules`.
 *  Shared with  → view and use ONLY. No edit, no rename, no delete, no share.
 *  Anyone else  → no access at all.
 *
 * SECURITY: this mirrors what the server must enforce (modules_router.py's
 * `_may_share`/`_may_edit`). Every mutating endpoint re-runs these checks
 * against the authenticated principal — a client-side check is not an
 * access boundary.
 */

import type { AppModule, User } from '../types';

export interface ModuleAccess {
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canShare: boolean;
  /** This user created the module. */
  isOwner: boolean;
  /** Visible to this user only because an Admin shared it with them. */
  isShared: boolean;
}

const NO_ACCESS: ModuleAccess = {
  canView: false,
  canEdit: false,
  canDelete: false,
  canShare: false,
  isOwner: false,
  isShared: false,
};

export function getModuleAccess(
  user: User | null,
  module: AppModule | null,
  /** Whether the CALLER's own role holds `modules:share` — resolved by the
   *  caller via `usePermissions().can('modules:share')`, since this file has
   *  no hook access of its own. Defaults `false`, so an untouched call site
   *  keeps today's behaviour (owner alone still cannot share) rather than
   *  silently granting it. */
  canShareModules = false,
): ModuleAccess {
  if (!user || !module) return NO_ACCESS;

  const isOwner = module.ownerId === user.id;
  /*
   * Anything reaching this function came from the caller's own already-
   * scoped module set — `GET /modules` (and the single-module fetch behind
   * a deep link) only ever returns what THIS user may see: owned, directly
   * shared, or granted through a group (see modules_router.py's
   * `_visible_modules`/`_load_visible`, which resolve all three).
   *
   * `user.assignedModuleIds` was used here before, but it mirrors only the
   * direct-share table (`services.py`'s `assigned_module_ids` queries
   * `ModuleAccess` alone) — it has no field for a group grant at all. A
   * module someone could only see because of a group membership was
   * therefore already correctly included by the server, then silently
   * refused access right here: the home page card would render (or not,
   * depending on the caller) but opening it hit "You do not have access to
   * this module" regardless.
   *
   * A module that isn't owned but still showed up in that set can only be a
   * direct share or a group grant — and both mean exactly the same thing
   * per this file's own rule above: view and use, never edit. So presence
   * in the set (not ownership) is what "shared with me" actually means.
   */
  const isSharedWithUser = !isOwner;

  // Admin override — manage all modules, including other users'.
  if (user.role === 'admin') {
    return {
      canView: true,
      canEdit: true,
      canDelete: true,
      canShare: true,
      isOwner,
      // An admin viewing someone else's module isn't a "shared" badge case.
      isShared: false,
    };
  }

  if (isOwner) {
    return {
      canView: true,
      canEdit: true,
      canDelete: true,
      // The owner's own `modules:share` grant decides this — see the
      // parameter's own doc comment above. A plain owner without it still
      // cannot share, same as before this permission existed.
      canShare: canShareModules,
      isOwner: true,
      isShared: false,
    };
  }

  if (isSharedWithUser) {
    // Read-only: view and use, but never modify.
    return {
      canView: true,
      canEdit: false,
      canDelete: false,
      canShare: false,
      isOwner: false,
      isShared: true,
    };
  }

  return NO_ACCESS;
}

export const canViewModule = (user: User | null, module: AppModule | null) =>
  getModuleAccess(user, module).canView;

export const canEditModule = (user: User | null, module: AppModule | null) =>
  getModuleAccess(user, module).canEdit;

export const canDeleteModule = (user: User | null, module: AppModule | null) =>
  getModuleAccess(user, module).canDelete;

export const canShareModule = (user: User | null, module: AppModule | null, canShareModules = false) =>
  getModuleAccess(user, module, canShareModules).canShare;

/** Thrown by the guarded moduleService mutators when a check fails. */
export class ModulePermissionError extends Error {
  constructor(action: 'edit' | 'delete' | 'share', moduleTitle?: string) {
    super(
      `Permission denied: you cannot ${action} ${
        moduleTitle ? `"${moduleTitle}"` : 'this module'
      }.`,
    );
    this.name = 'ModulePermissionError';
  }
}
